import { Clear, RotateClockwise, RotateCounterClockwise } from "@procore/core-icons";
import { useEffect, useMemo } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { useResetView } from "features/home/useResetView";
import {
    selectCanClear,
    selectIsRedoEmpty,
    selectIsUndoEmpty,
    selectPeekRedo,
    selectPeekUndo,
    undoRedoActions,
} from "features/undoRedo";
import { useHasSceneModifications } from "features/undoRedo/useHasSceneModifications";
import ResetIcon from "media/icons/reset.svg?react";
import { isMacintosh } from "utils/misc";

import { Toolbar } from "../Toolbar";

interface UndoRedoToolbarProps {
    id?: string;
}

// Useful for debugging undo/redo stack w/out reloading
const SHOW_CLEAR_BUTTON = false;

/**
 * UndoRedo toolbar with step back/forward buttons
 */
export function UndoRedoToolbar({ id = "undo-redo-toolbar" }: UndoRedoToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();

    const dispatch = useAppDispatch();

    const nextUndo = useAppSelector(selectPeekUndo);
    const nextRedo = useAppSelector(selectPeekRedo);
    const isUndoEmpty = useAppSelector(selectIsUndoEmpty);
    const isRedoEmpty = useAppSelector(selectIsRedoEmpty);
    const canClear = useAppSelector(selectCanClear);
    const resetView = useResetView();
    const hasSceneModifications = useHasSceneModifications();

    const buttons = useMemo(() => {
        const mainButtons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "reset",
                    icon: <ResetIcon />,
                    onClick: async () => {
                        await resetView({
                            resetCamera: false,
                            resetVersionComparisonPropertyTree: false,
                            preservePicker: true,
                        });
                        void track(Events.toolbar.resetTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Reset",
                    tooltip: {
                        toolName: "reset",
                        alwaysVisible: false,
                        placement: "left",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: !hasSceneModifications,
                    visible: true,
                },
            },
            {
                config: {
                    id: "undo",
                    icon: <RotateCounterClockwise />,
                    onClick: () => {
                        if (nextUndo === undefined) {
                            return;
                        }

                        nextUndo.undoCallback();
                        dispatch(undoRedoActions.undo());
                        void track(Events.toolbar.undoTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Undo",
                    tooltip: {
                        toolName: "undo",
                        shortcutKey: isMacintosh ? "Cmd+Z" : "Ctrl+Z",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: false,
                    disabled: isUndoEmpty,
                    visible: true,
                },
            },
            {
                config: {
                    id: "redo",
                    icon: <RotateClockwise />,
                    onClick: () => {
                        if (nextRedo === undefined) {
                            return;
                        }
                        nextRedo.redoCallback();
                        dispatch(undoRedoActions.redo());
                        void track(Events.toolbar.redoTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Redo",
                    tooltip: {
                        toolName: "redo",
                        shortcutKey: isMacintosh ? "Cmd+Shift+Z / Cmd+Y" : "Ctrl+Y",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: false,
                    disabled: isRedoEmpty,
                    visible: true,
                },
            },
        ];
        if (SHOW_CLEAR_BUTTON) {
            mainButtons.push({
                config: {
                    id: "clear",
                    icon: <Clear />,
                    onClick: () => {
                        dispatch(undoRedoActions.clear());
                    },
                    title: "Clear",
                },
                state: {
                    active: false,
                    disabled: canClear,
                    visible: true,
                },
            });
        }

        return mainButtons;
    }, [isUndoEmpty, isRedoEmpty, canClear, dispatch, nextUndo, nextRedo, resetView, hasSceneModifications]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, buttons]);

    return <Toolbar id={id} />;
}
